import {
  AloftPrice,
  DefaultEcoLifeField,
  EditableSubjectData,
  Improvements,
  defaultEcoLifeFields,
  isDefined,
  isUndefined,
  orUndefined,
  schPositiveInt,
} from '@aloft/toolkit-shared';

import { DEPRECIATION_SEGMENTS } from '../../../constants.ts';
import { PropertyDataSliceExtraReducer } from '../../../types.ts';
import { setDepreciation } from '../../actions/setDepreciation.ts';
import { setSettings } from '../../actions/setSettings.ts';
import { setTotalEconomicLife } from '../../actions/setTotalEconomicLife.ts';
import { sliderAdjustment } from '../../actions/sliderAdjustment.ts';
import { propertyDataAdapter } from '../../propertyDataAdapter.ts';
import { getChangedEntityFromEditableData } from '../utils/getChangedEntityFromEditableData.ts';
import { getCurrentImprovements } from '../utils/getCurrentImprovements.ts';

export const improvementsExtraReducers: PropertyDataSliceExtraReducer = (builder) => {
  builder.addCase(sliderAdjustment, (state, action) => {
    const { id, field, newValue } = action.payload;
    // Allow undefined/null for craftsmanQuality to represent empty state
    if (field === 'craftsmanQuality' && newValue !== undefined && newValue < 1) return;

    let current = state.entities[id];

    if (id === 'subjectArv') {
      current = state.entities.subject;
      if (!current) return;

      const currentImprovements = getCurrentImprovements(current, true);
      if (!currentImprovements) return;

      propertyDataAdapter.updateOne(
        state,
        getChangedEntityFromEditableData(current, {
          arv: {
            improvements: {
              ...currentImprovements,
              [field]: newValue,
            },
          },
        }),
      );
    } else {
      if (!current) return;

      const currentImprovements = getCurrentImprovements(current);
      if (!currentImprovements) return;

      propertyDataAdapter.updateOne(
        state,
        getChangedEntityFromEditableData(current, {
          improvements: {
            ...currentImprovements,
            [field]: newValue,
          },
        }),
      );
    }
  });

  builder.addCase(setDepreciation, (state, action) => {
    const { id, field, newValue } = action.payload;

    let current = state.entities[id];
    const writingToArv = id === 'subjectArv';
    if (writingToArv) {
      current = state.entities.subject;
    }
    if (!current) return;

    const price = orUndefined((val: number) => AloftPrice.fromDollarLike(val).toJSON())(newValue);
    const currentImprovements = getCurrentImprovements(current, writingToArv);
    if (!currentImprovements) return;

    const changes: Partial<EditableSubjectData> = {
      ...(writingToArv
        ? {
            arv: {
              improvements: {
                ...currentImprovements,
                [field]: price,
              },
            },
          }
        : {
            improvements: {
              ...currentImprovements,
              [field]: price,
            },
          }),
    };

    const changedEntity = getChangedEntityFromEditableData(current, changes);

    propertyDataAdapter.updateOne(state, changedEntity);
  });

  builder.addCase(setTotalEconomicLife, (state, action) => {
    const { id, field, newValue } = action.payload;
    const updated = schPositiveInt.safeParse(newValue);
    const current = state.entities[id];

    if (!updated.success || !current) return;

    // Lower effective age if it's above new total economic life
    const { effectiveAgeField = 'effectiveAge' } =
      DEPRECIATION_SEGMENTS.find(({ ecoLifeField }) => ecoLifeField === field) ?? {};

    const currentImprovements = getCurrentImprovements(current);
    if (!currentImprovements) return;
    const currentEffectiveAge = currentImprovements[effectiveAgeField];

    const changes = { [field]: updated.data };
    if (isDefined(currentEffectiveAge) && currentEffectiveAge > updated.data) {
      changes[effectiveAgeField] = updated.data;
    }

    propertyDataAdapter.updateOne(
      state,
      getChangedEntityFromEditableData(current, {
        improvements: {
          ...currentImprovements,
          ...changes,
        },
      }),
    );
  });

  builder.addCase(setSettings, (state, action) => {
    state.ids.forEach((id) => {
      const current = state.entities[id];
      if (!current) return;

      const currentImprovements = getCurrentImprovements(current);
      if (!currentImprovements) return;
      const ecoLifeChanges: Partial<Improvements> = {};

      const ecoLifeFields: DefaultEcoLifeField[] = defaultEcoLifeFields.filter(
        (field) => field !== 'defaultEcoLife' && field !== 'defaultEcoLifeManufactured',
      );

      if (currentImprovements.propertyType === 'manufacturedHome') {
        ecoLifeFields.push('defaultEcoLifeManufactured');
      } else {
        ecoLifeFields.push('defaultEcoLife');
      }

      ecoLifeFields.forEach((field) => {
        const defaultEcoLife = action.payload[field];
        if (defaultEcoLife === undefined) return;

        const segment = DEPRECIATION_SEGMENTS.find((s) => s.defaultEcoLifeField === field) ?? {
          effectiveAgeField: 'effectiveAge',
          ecoLifeField: 'totalEconomicLife',
        };

        const currentEffAge = currentImprovements[segment.effectiveAgeField];
        const currentEcoLife = currentImprovements[segment.ecoLifeField];

        if (
          isUndefined(currentEcoLife) &&
          isDefined(currentEffAge) &&
          currentEffAge > defaultEcoLife
        ) {
          ecoLifeChanges[segment.effectiveAgeField] = schPositiveInt.parse(defaultEcoLife);
        }
      });

      const newImprovements = { ...currentImprovements, ...ecoLifeChanges };

      propertyDataAdapter.updateOne(
        state,
        getChangedEntityFromEditableData(current, { improvements: newImprovements }),
      );
    });
  });
};
